import { Injectable, inject, PLATFORM_ID, signal, DOCUMENT } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isPlatformBrowser } from '@angular/common';
import { SUPPORTED_LANGUAGE_CODES } from './languages';


@Injectable({
  providedIn: 'root',
})
export class LocalizationService {
  public currentLang = signal<string>('en');
  private translateService = inject(TranslateService);
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);

  private readonly STORAGE_KEY = 'app_language';
  private readonly DEFAULT_LANG = 'en';
  private readonly SUPPORTED_LANGS = SUPPORTED_LANGUAGE_CODES;

  initialize(lang?: string): Promise<void> {
    this.translateService.setDefaultLang(lang || this.DEFAULT_LANG);

    let currentLang = lang || this.DEFAULT_LANG;

    if (isPlatformBrowser(this.platformId) && !lang) {
      const storedLang = this.readStoredLang();
      if (storedLang && this.SUPPORTED_LANGS.includes(storedLang)) {
        currentLang = storedLang;
      } else {
        const browserLang = this.translateService.getBrowserLang();
        if (browserLang && this.SUPPORTED_LANGS.includes(browserLang)) {
          currentLang = browserLang;
        }
      }
    }

    this.currentLang.set(currentLang);

    return this.setLanguage(currentLang);
  }

  setLanguage(lang: string): Promise<void> {
    if (!this.SUPPORTED_LANGS.includes(lang)) {
      lang = this.DEFAULT_LANG;
    }

    // Store language preference in localStorage
    if (isPlatformBrowser(this.platformId)) {
      this.writeStoredLang(lang);
    }

    // Update document lang attribute (also during SSR)
    this.document.documentElement.lang = lang;

    // Use the TranslateService to set the language
    return new Promise<void>(resolve => {
      this.translateService.use(lang).subscribe(() => {
        this.currentLang.set(lang);
        resolve();
      });
    });
  }

  // localStorage may be absent or throw even in a "browser" platform:
  // Safari private mode, blocked third-party storage, torn-down test envs.
  private readStoredLang(): string | null {
    try {
      return typeof localStorage !== 'undefined' ? localStorage.getItem(this.STORAGE_KEY) : null;
    } catch {
      return null;
    }
  }

  private writeStoredLang(lang: string): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.STORAGE_KEY, lang);
      }
    } catch {
      // Preference simply will not persist — not worth breaking the app for.
    }
  }

  getCurrentLang(): string {
    return this.translateService.currentLang || this.DEFAULT_LANG;
  }

  getSupportedLanguages(): string[] {
    return [...this.SUPPORTED_LANGS];
  }
}
